/**
 * Avatar encoding for the Account settings page: the picked file becomes a
 * small square data URL before it is posted, so the platform `User` row
 * stores a self-contained image and no blob store is involved.
 *
 * @module components/settings/AccountSettings.logic
 */

/** Stored avatar edge length. Matches the largest rendered size (32px) with retina headroom. */
export const AVATAR_EDGE_PX = 256;

/** Reject absurd picks before decoding; the encoder shrinks everything else. */
export const AVATAR_MAX_FILE_BYTES = 20 * 1024 * 1024;

export interface CropRect {
  readonly sx: number;
  readonly sy: number;
  readonly size: number;
}

/** The centered square to cut from a `width`×`height` image (cover crop). */
export function coverCropRect(width: number, height: number): CropRect {
  const size = Math.min(width, height);
  return {
    sx: Math.floor((width - size) / 2),
    sy: Math.floor((height - size) / 2),
    size,
  };
}

/**
 * Decode, center-crop and downscale the picked image, returning a data URL
 * ready for `updateProfilePicture`. Prefers WebP; canvases without WebP
 * support fall back to JPEG (both pass the server's validator).
 */
export async function encodeAvatarFile(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Pick an image file.");
  }
  if (file.size > AVATAR_MAX_FILE_BYTES) {
    throw new Error("Pick an image under 20MB.");
  }
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error("That image could not be read.");
  });
  try {
    const { sx, sy, size } = coverCropRect(bitmap.width, bitmap.height);
    const edge = Math.min(AVATAR_EDGE_PX, size);
    const canvas = document.createElement("canvas");
    canvas.width = edge;
    canvas.height = edge;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("That image could not be read.");
    }
    context.drawImage(bitmap, sx, sy, size, size, 0, 0, edge, edge);
    const webp = canvas.toDataURL("image/webp", 0.85);
    return webp.startsWith("data:image/webp") ? webp : canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    bitmap.close();
  }
}
